import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import ConfirmDialog from '../components/ConfirmDialog';
import { useT } from '../i18n/i18n';
import type { School, SchoolClass, Staff } from './schoolApi';

function TeacherPicker({ staff, chosen, onChange, idPrefix }: {
  staff: Staff[];
  chosen: string[];
  onChange: (ids: string[]) => void;
  idPrefix: string;
}) {
  const t = useT();
  const active = staff.filter((s) => s.active);
  return (
    <fieldset className="school-checks">
      <legend>{t('Teachers')}</legend>
      {active.map((s) => (
        <label className="check" key={s.id} htmlFor={`${idPrefix}-${s.id}`}>
          <input
            id={`${idPrefix}-${s.id}`}
            type="checkbox"
            checked={chosen.includes(s.id)}
            onChange={(e) => onChange(e.target.checked ? [...chosen, s.id] : chosen.filter((x) => x !== s.id))}
          />
          {s.name ?? s.email}
        </label>
      ))}
    </fieldset>
  );
}

export default function ClassesTab({ school }: { school: School }) {
  const t = useT();
  const admin = school.role === 'ADMIN';
  const [classes, setClasses] = useState<SchoolClass[] | null>(null);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [teachers, setTeachers] = useState<string[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editTeachers, setEditTeachers] = useState<string[]>([]);
  const [deleting, setDeleting] = useState<SchoolClass | null>(null);

  const load = useCallback(async () => {
    try {
      setClasses(await api<SchoolClass[]>('/school/classes', { token: true }));
      if (admin) setStaff(await api<Staff[]>('/school/staff', { token: true }));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('Could not load the classes.'));
    }
  }, [admin, t]);

  useEffect(() => { void load(); }, [load]);

  async function run(action: () => Promise<unknown>) {
    setError(null);
    setBusy(true);
    try {
      await action();
      await load();
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : t('That did not work. Please try again.'));
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function add(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const ok = await run(() => api('/school/classes', { method: 'POST', token: true, body: { name: name.trim(), teacherIds: teachers } }));
    if (ok) { setName(''); setTeachers([]); }
  }

  async function save(id: string) {
    const ok = await run(() => api(`/school/classes/${id}`, { method: 'PATCH', token: true, body: { name: editName.trim(), teacherIds: editTeachers } }));
    if (ok) setEditing(null);
  }

  return (
    <div className="spaced">
      <div className="card">
        <h3>{admin ? t('Classes') : t('Your classes')}</h3>
        <p className="field-hint" style={{ marginTop: 0 }}>
          {t('A teacher sees only the pupils in the classes they teach. School admins see every pupil.')}
        </p>
        {error && <div className="error" role="alert">{error}</div>}
        {classes === null ? (
          <p className="muted">{t('Loading…')}</p>
        ) : classes.length === 0 ? (
          <p className="muted">{admin ? t('No classes yet. Add the first one below.') : t('You are not teaching a class yet. A school admin can add you to one.')}</p>
        ) : (
          <ul className="list">
            {classes.map((c) => (
              <li className="list-item" key={c.id}>
                {editing === c.id ? (
                  <div className="spaced" style={{ flex: 1 }}>
                    <div className="field">
                      <label htmlFor={`class-name-${c.id}`}>{t('Class name')}</label>
                      <input id={`class-name-${c.id}`} value={editName} maxLength={80} onChange={(e) => setEditName(e.target.value)} />
                    </div>
                    <TeacherPicker staff={staff} chosen={editTeachers} onChange={setEditTeachers} idPrefix={`class-teacher-${c.id}`} />
                    <div className="toolbar">
                      <button type="button" className="btn" onClick={() => void save(c.id)} disabled={busy || !editName.trim()}>{t('Save')}</button>
                      <button type="button" className="btn ghost" onClick={() => setEditing(null)} disabled={busy}>{t('Cancel')}</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="list-item-text">
                      <div className="name">{c.name}</div>
                      <div className="muted">
                        {t('{n} pupils', { n: c.pupils })}
                        {' · '}
                        {c.teachers.length ? c.teachers.map((x) => x.name).join(', ') : t('No teacher yet')}
                      </div>
                    </div>
                    {admin && (
                      <div className="toolbar">
                        <button type="button" className="btn secondary" onClick={() => { setEditing(c.id); setEditName(c.name); setEditTeachers(c.teachers.map((x) => x.id)); }}>
                          {t('Change')}
                        </button>
                        <button type="button" className="btn quiet-danger" onClick={() => setDeleting(c)}>{t('Delete')}</button>
                      </div>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {admin && (
        <form className="card spaced" onSubmit={add}>
          <h3>{t('Add a class')}</h3>
          <div className="field">
            <label htmlFor="class-new-name">{t('Class name')}</label>
            <input id="class-new-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
          </div>
          <TeacherPicker staff={staff} chosen={teachers} onChange={setTeachers} idPrefix="class-new-teacher" />
          <div className="toolbar">
            <button type="submit" className="btn" disabled={busy || !name.trim()}>{t('Add class')}</button>
          </div>
        </form>
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={t('Delete {name}?', { name: deleting?.name ?? '' })}
        message={t('The class is deleted. Its pupils and their records stay; they are no longer in this class.')}
        confirmLabel={t('Delete class')}
        destructive
        onConfirm={() => {
          const c = deleting;
          setDeleting(null);
          if (c) void run(() => api(`/school/classes/${c.id}`, { method: 'DELETE', token: true }));
        }}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
